import React from "react";
import { NavLink } from "react-router-dom";
import {
  Chip,
  Collapse,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

type SidebarNavListItemProps = {
  depth: number;
  href: string;
  icon?: React.ElementType;
  badge?: string;
  open?: boolean;
  title: string;
  collapsed?: boolean;
  children?: React.ReactNode;
};

/** Nav item look: 7px 10px, radius 4, 13px secondary text; active = tint + brand text. */
const itemSx = (depth: number, collapsed: boolean) => (theme: Theme) => {
  // primary.dark is the darker brand on light and the lighter brand on dark,
  // so the active text clears 4.5:1 on brandTint in both modes.
  const activeColor = theme.palette.primary.dark;
  return {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minHeight: 34,
    padding: collapsed ? "7px 0" : `7px 10px 7px ${10 + depth * 18}px`,
    justifyContent: collapsed ? "center" : "flex-start",
    borderRadius: "4px",
    color: theme.palette.text.secondary,
    fontSize: "0.8125rem",
    "& .MuiSvgIcon-root, & svg": {
      fontSize: 18,
      width: 18,
      height: 18,
      flex: "none",
      color: theme.palette.text.disabled,
    },
    "&:hover": { backgroundColor: theme.palette.action.hover },
    "&.active": {
      backgroundColor: theme.palette.brandTint,
      color: activeColor,
      fontWeight: 500,
      "& .MuiSvgIcon-root, & svg": { color: activeColor },
    },
  };
};

const titleSx = {
  margin: 0,
  "& .MuiListItemText-primary": {
    fontSize: "inherit",
    fontWeight: "inherit",
    color: "inherit",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

const SidebarNavListItem: React.FC<SidebarNavListItemProps> = ({
  title,
  href,
  depth = 0,
  children,
  icon: Icon,
  badge,
  open: openProp = false,
  collapsed = false,
}) => {
  const [open, setOpen] = React.useState(openProp);

  const label = !collapsed && <ListItemText primary={title} sx={titleSx} />;
  const badgeChip = !collapsed && badge && (
    <Chip label={badge} size="small" sx={{ height: 20, fontSize: 11 }} />
  );
  const withTooltip = (node: React.ReactElement) =>
    collapsed ? (
      <Tooltip title={title} placement="right">
        {node}
      </Tooltip>
    ) : (
      node
    );

  if (children) {
    return (
      <>
        {withTooltip(
          <ListItemButton
            onClick={() => setOpen((state) => !state)}
            aria-expanded={open}
            aria-label={collapsed ? title : undefined}
            sx={itemSx(depth, collapsed)}
          >
            {Icon && <Icon aria-hidden />}
            {label}
            {badgeChip}
            {!collapsed && (open ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
        )}
        {!collapsed && (
          <Collapse in={open} unmountOnExit>
            {children}
          </Collapse>
        )}
      </>
    );
  }

  return withTooltip(
    <ListItemButton
      component={NavLink}
      to={href}
      aria-label={collapsed ? title : undefined}
      sx={itemSx(depth, collapsed)}
    >
      {Icon && <Icon aria-hidden />}
      {label}
      {badgeChip}
    </ListItemButton>
  );
};

export default SidebarNavListItem;
